<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { IconFlask } from '@tabler/icons-vue'

/**
 * S0 §55.3 rule 2 and P1 §4: every page of a DEMONSTRATION workspace carries
 * this, and nothing can dismiss it. The shell draws it, so a page cannot
 * forget it: `compact` beside the product's name in the header, and the full
 * line above the page where the header has no room for it (a phone).
 */
defineProps<{ compact?: boolean }>()
const { t } = useI18n()
</script>

<template>
  <span
    v-if="compact"
    class="demo-chip"
    role="note"
    :title="t('workspace.demonstrationExplainer')"
    data-test="demonstration-marker"
  >
    <IconFlask :size="14" aria-hidden="true" />
    {{ t('workspace.demonstrationMarker') }}
  </span>
  <div v-else class="demo-marker" role="note" data-test="demonstration-marker">
    <IconFlask :size="16" aria-hidden="true" />
    <strong class="demo-marker__label">{{ t('workspace.demonstrationMarker') }}</strong>
    <span class="demo-marker__text">{{ t('workspace.demonstrationExplainer') }}</span>
  </div>
</template>

<style scoped>
.demo-marker {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--spacing-sm) var(--spacing-md);
  margin-bottom: var(--spacing-lg);
  padding: var(--spacing-sm) var(--spacing-lg);
  border: 1px solid var(--alert-warning-line);
  border-radius: var(--radius-lg);
  background: var(--alert-warning-bg);
  color: var(--fg-primary);
  font-size: var(--font-size-body-3);
  line-height: var(--line-height-body-3);
}

.demo-marker > svg,
.demo-chip > svg {
  color: var(--alert-warning-accent);
}

.demo-marker__label {
  font-weight: var(--font-weight-semibold);
}

.demo-marker__text {
  color: var(--fg-secondary);
}

/* The same warning colours as the line, as a pill that holds its own on the navy header. */
.demo-chip {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xxs) var(--spacing-md);
  border: 1px solid var(--alert-warning-line);
  border-radius: var(--radius-full);
  background: var(--alert-warning-bg);
  color: var(--fg-primary);
  font-family: var(--font-family-primary);
  font-size: var(--font-size-caption);
  line-height: var(--line-height-caption);
  font-weight: var(--font-weight-semibold);
  white-space: nowrap;
}
</style>
